import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import Confetti from 'react-confetti';
import RegulationModal, { RegulationBadge } from '@/components/RegulationModal';
import { 
    CheckCircle2, XCircle, AlertTriangle, Play, 
    ChevronRight, Zap, FileText, BookOpen
} from 'lucide-react';

const QuizResults = ({
    lesson,
    topic,
    questions,
    answers,
    results,
    xpEarned,
    showConfetti,
    nextLesson,
    hasQuestions,
    onRetry,
    regulationModalOpen,
    selectedRegulation,
    setRegulationModalOpen,
    setSelectedRegulation
}) => {
    const navigate = useNavigate();
    
    // Modül ID'sini bul
    const getModuleId = () => {
        if (topic?.module_id) return topic.module_id;
        // Fallback: topic_id'den çıkar (T01-01 -> M01)
        if (lesson?.topic_id) {
            const match = lesson.topic_id.match(/T(\d+)/);
            if (match) return `M${match[1]}`;
        }
        return null;
    };
    
    const moduleId = getModuleId();
    
    // Yanlış cevaplanan soruların mevzuat referanslarını topla
    const wrongAnswerRefs = questions
        .filter(q => !answers[q.id]?.correct && q.regulation_reference)
        .map(q => q.regulation_reference)
        .filter((ref, idx, arr) => {
            const key = `${ref.law_id}-${ref.article}`;
            return arr.findIndex(r => `${r.law_id}-${r.article}` === key) === idx;
        });
    
    return (
        <Card className="text-center relative overflow-hidden">
            {showConfetti && <Confetti recycle={false} numberOfPieces={200} />}
            
            <CardContent className="p-8 space-y-6">
                {hasQuestions ? (
                    <>
                        <motion.div 
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: "spring", duration: 0.5 }}
                            className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center bg-gradient-to-r ${
                                results.percentage >= 80 ? 'from-green-500 to-emerald-500' : 'from-amber-500 to-orange-500'
                            }`}
                        >
                            {results.percentage >= 80 ? (
                                <CheckCircle2 className="w-10 h-10 text-white" />
                            ) : (
                                <AlertTriangle className="w-10 h-10 text-white" />
                            )}
                        </motion.div>
                        
                        <div>
                            <h2 className="text-2xl font-bold mb-2">
                                {results.percentage >= 80 ? 'Tebrikler! Dersi Gectiniz' : 'Maalesef Gecemediniz'}
                            </h2>
                            <p className="text-muted-foreground">
                                {results.correct} / {results.total} soru doğru
                            </p>
                            {results.percentage < 70 && (
                                <p className="text-sm text-amber-400 mt-2">
                                    Dersi geçmek için en az %80 basari gereklidir.
                                </p>
                            )}
                        </div>
                        
                        <motion.div 
                            initial={{ scale: 0.5, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ delay: 0.2 }}
                            className="text-5xl font-bold text-primary"
                        >
                            %{results.percentage}
                        </motion.div>

                        {/* Bloom Analysis */}
                        {(() => {
                            const bloomStats = {};
                            const scenarioStats = { scenario: { correct: 0, total: 0 }, theory: { correct: 0, total: 0 } };
                            questions.forEach(q => {
                                const bl = q.bloom_level || 'hatırlama';
                                if (!bloomStats[bl]) bloomStats[bl] = { correct: 0, total: 0 };
                                bloomStats[bl].total++;
                                if (answers[q.id]?.correct) bloomStats[bl].correct++;
                                const isScenario = q.question_type === 'scenario' || q.is_scenario;
                                const key = isScenario ? 'scenario' : 'theory';
                                scenarioStats[key].total++;
                                if (answers[q.id]?.correct) scenarioStats[key].correct++;
                            });
                            const bloomEntries = Object.entries(bloomStats);
                            const hasBloom = bloomEntries.length > 1;
                            const hasScenario = scenarioStats.scenario.total > 0;
                            if (!hasBloom && !hasScenario) return null;
                            return (
                                <div className="text-left space-y-3 p-4 rounded-xl bg-muted/30 border border-border/50">
                                    {hasBloom && (<>
                                        <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/40">Bilissel Seviye Analizi</div>
                                        <div className="space-y-1.5">
                                            {bloomEntries.map(([level, s]) => {
                                                const pct = s.total > 0 ? Math.round(s.correct / s.total * 100) : 0;
                                                return (
                                                    <div key={level} className="flex items-center gap-2">
                                                        <span className="text-[10px] text-muted-foreground w-20 capitalize">{level}</span>
                                                        <div className="flex-1 h-1.5 rounded-full bg-muted/50"><div className={`h-full rounded-full ${pct >= 80 ? 'bg-emerald-400' : pct >= 50 ? 'bg-amber-400' : 'bg-red-400'}`} style={{width:`${pct}%`}} /></div>
                                                        <span className={`text-[10px] font-medium w-10 text-right ${pct >= 80 ? 'text-emerald-400' : pct >= 50 ? 'text-amber-400' : 'text-red-400'}`}>%{pct}</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </>)}
                                    {hasScenario && (
                                        <div className="flex gap-3 pt-2 border-t border-border/30">
                                            <div className="flex-1 p-2 rounded-lg bg-muted/30 text-center">
                                                <div className="text-[10px] text-muted-foreground/40">Teorik</div>
                                                <div className="text-sm font-bold">%{scenarioStats.theory.total > 0 ? Math.round(scenarioStats.theory.correct / scenarioStats.theory.total * 100) : 0}</div>
                                            </div>
                                            <div className="flex-1 p-2 rounded-lg bg-muted/30 text-center">
                                                <div className="text-[10px] text-muted-foreground/40">Senaryo</div>
                                                <div className="text-sm font-bold">%{scenarioStats.scenario.total > 0 ? Math.round(scenarioStats.scenario.correct / scenarioStats.scenario.total * 100) : 0}</div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })()}
                        
                        <div>
                            {results.percentage >= 80 ? (
                                <Badge className="text-lg px-4 py-2 bg-green-500/20 text-green-400">
                                    <CheckCircle2 className="w-5 h-5 mr-2" />
                                    BAŞARILI - GEÇTİNİZ
                                </Badge>
                            ) : (
                                <Badge className="text-lg px-4 py-2 bg-red-500/20 text-red-400">
                                    <XCircle className="w-5 h-5 mr-2" />
                                    BAŞARISIZ - KALDINIZ
                                </Badge>
                            )}
                        </div>
                    </>
                ) : (
                    <>
                        <motion.div 
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: "spring", duration: 0.5 }}
                            className="w-20 h-20 mx-auto rounded-full flex items-center justify-center bg-gradient-to-r from-emerald-500 to-green-500"
                        >
                            <CheckCircle2 className="w-10 h-10 text-white" />
                        </motion.div>
                        
                        <div>
                            <h2 className="text-2xl font-bold mb-2">Ders Tamamlandı!</h2>
                            <p className="text-muted-foreground">
                                Bu dersin tüm içeriğini başarıyla tamamladınız.
                            </p>
                        </div>
                        
                        <div>
                            <Badge className="text-lg px-4 py-2 bg-green-500/20 text-green-400">
                                <CheckCircle2 className="w-5 h-5 mr-2" />
                                TAMAMLANDI
                            </Badge>
                        </div>
                    </>
                )}
                
                {xpEarned > 0 && (
                    <motion.div
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.4 }}
                        className="flex items-center justify-center gap-2"
                    >
                        <Badge className="text-lg px-4 py-2 bg-amber-500/20 text-amber-400 border-amber-500/30">
                            <Zap className="w-5 h-5 mr-2" />
                            +{xpEarned} XP Kazandın!
                        </Badge>
                    </motion.div>
                )}
                
                {nextLesson?.has_next && (
                    <motion.div
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.6 }}
                        className="pt-4 border-t border-border"
                    >
                        <p className="text-sm text-muted-foreground mb-3">
                            {nextLesson.same_topic ? 'Aynı konuda devam et' : `Yeni Konu: ${nextLesson.new_topic}`}
                        </p>
                        <Button 
                            type="button"
                            size="lg" 
                            data-testid="next-lesson-btn"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const nextId = nextLesson?.lesson?.id;
                                if (nextId) {
                                    navigate(`/lesson/${nextId}`);
                                }
                            }}
                            className="bg-primary hover:bg-primary/90 cursor-pointer"
                        >
                            <Play className="w-5 h-5 mr-2" />
                            Sonraki Ders: {nextLesson.lesson.name}
                            <ChevronRight className="w-5 h-5 ml-2" />
                        </Button>
                    </motion.div>
                )}
                
                {/* Soru Detayları */}
                {hasQuestions && (
                    <div className="text-left border-t pt-6 space-y-4">
                        {wrongAnswerRefs.length > 0 && (
                            <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30 mb-4">
                                <h4 className="font-semibold text-amber-400 mb-3 flex items-center gap-2">
                                    <BookOpen className="w-4 h-4" />
                                    Tekrar Etmeniz Gereken Mevzuatlar
                                </h4>
                                <p className="text-sm text-muted-foreground mb-3">
                                    Yanlış cevapladığınız sorular aşağıdaki mevzuatlarla ilgili.
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {wrongAnswerRefs.map((ref, idx) => (
                                        <RegulationBadge 
                                            key={idx}
                                            reference={ref}
                                            onClick={() => {
                                                setSelectedRegulation(ref);
                                                setRegulationModalOpen(true);
                                            }}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                        
                        <h3 className="font-semibold flex items-center gap-2">
                            <FileText className="w-4 h-4" />
                            Soru Detayları
                        </h3>
                        {Object.keys(answers).length === 0 ? (
                            <div className="p-4 rounded-lg border bg-secondary/50 border-border text-center">
                                <p className="text-muted-foreground text-sm">
                                    Bu quiz daha önce tamamlandı. Detaylı cevap bilgisi mevcut değil.
                                </p>
                            </div>
                        ) : (
                            questions.map((q, idx) => {
                                const answer = answers[q.id];
                                const isCorrect = answer?.correct;
                                const hasAnswer = answer !== undefined;
                                return (
                                    <div key={q.id} className={`p-4 rounded-lg border ${
                                        !hasAnswer ? 'bg-secondary/50 border-border' :
                                        isCorrect ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'
                                    }`}>
                                        <div className="flex items-start gap-3">
                                            {!hasAnswer ? (
                                                <FileText className="w-5 h-5 text-muted-foreground mt-0.5 flex-shrink-0" />
                                            ) : isCorrect ? (
                                                <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                                            ) : (
                                                <XCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                                            )}
                                            <div className="flex-1">
                                                <p className="font-medium text-sm mb-2">
                                                    {idx + 1}. {q.question_text}
                                                </p>
                                                {q.explanation && (
                                                    <div className="mt-2 p-3 rounded bg-secondary/50 text-xs text-muted-foreground">
                                                        <span className="font-medium text-foreground">Açıklama: </span>
                                                        {q.explanation}
                                                    </div>
                                                )}
                                                {q.regulation_reference && (
                                                    <div className="mt-2">
                                                        <RegulationBadge 
                                                            reference={q.regulation_reference}
                                                            onClick={() => {
                                                                setSelectedRegulation(q.regulation_reference);
                                                                setRegulationModalOpen(true);
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                )}
                
                <div className="flex gap-4 justify-center pt-4">
                    <Button variant="outline" onClick={() => navigate(moduleId ? `/module/${moduleId}` : '/dashboard')}>
                        Konulara Dön
                    </Button>
                    {results.percentage < 70 && hasQuestions && (
                        <Button onClick={onRetry}>
                            Tekrar Dene
                        </Button>
                    )}
                </div>
            </CardContent>
            
            <RegulationModal 
                reference={selectedRegulation}
                isOpen={regulationModalOpen}
                onClose={() => setRegulationModalOpen(false)}
            />
        </Card>
    );
};

export default QuizResults;
